import { PublicClientApplication } from "@azure/msal-browser";
import { msalConfig, loginRequest } from "../authConfig";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import axios from "axios";
import { isEmpty } from "lodash";
import { Assessment, WordResponseType } from "../common/store/assessmentSlice";
import { AssessmentForm } from "../common/store/assessmentFormSlice";
import { AccessToken, setAccessToken } from "../common/store/appAccessSlice";
import { User } from "../common/store/userInfoSlice";
import {
    Agreement,
    AssessmentDeletionMode,
    AssessmentHistory,
    AssessmentResult,
    AssessmentStatus,
    AvailableAssessment,
    BulkResponseApi,
    CreatePurchaseApiResponse,
    Documentation,
    DocumentationCategory,
    Entity,
    ExistingAssessments,
    ExistingExamineeDetails,
    getOrganizationsSortOptions,
    getOrganizationUsersSortOptions,
    InviteUser,
    Organizations,
    OrganizationUsers,
    OrganizationUserStatusId,
    Paginated,
    PricingTier,
    Purchase,
    PurchaseStatus,
    Role,
    RoleId,
    SortDirection,
    UserDetail,
    UserInfo,
    UserUpdate,
    WordRequests,
    WordResponseApi
} from "../common/store/generalTypes";
import {
    Organization,
    OrganizationApiResponse,
    OrganizationStatus,
    organizationToApi,
    PersonalInfo
} from "../common/helpers/organizationHelper";
import {
    ExistingExaminees,
    UpcomingAssessment,
    OrgLocation,
    OrgLocationApi,
    NewUpcomingAssessmentId as NewUpcomingAssessmentResponse,
    getLocationExamineesSortOptions
} from "@store/locationsSlice";
import {
    Examinee,
    NewExamineeApi,
    NewExamineeId,
    parseNewExamineeForApi
} from "@fadmin/tabs/assessments/store/newExamineeSlice";
import { format } from "date-fns";
import { Env } from "../env";
import qs from "qs";
import { getEnumKeyByValue } from "@helpers/enumHelper";

const msalInstance = new PublicClientApplication(msalConfig);

const getToken = async (): Promise<string> => {
    const activeAccount = msalInstance.getActiveAccount();
    const accounts = msalInstance.getAllAccounts();

    const request = {
        ...loginRequest,
        account: activeAccount ?? accounts[0],
        forceRefresh: true
    };

    const authResult = await msalInstance.acquireTokenSilent(request);

    return authResult.accessToken;
};

const ELAPSED_TIMEOUT = 8000;

export const ApiService = (): ApiServiceProps => {
    const dispatch = useAppDispatch();
    const accessToken = useAppSelector((x) => x.appAccess.accessToken);

    /**
     * User logged is required to request api.
     * @param path - relative url
     * @param options - api options
     * @returns -
     */
    const protectedRequest = async <T = any>(path: string, options: HttpOptions): Promise<T> => {
        const token = await getToken();
        return await createRequest(path, options, token);
    };

    /**
     * User logged is not required to request api
     * @param path - relative url
     * @param options - api options
     * @returns -
     */
    const appProtectedRequest = async <T = any>(path: string, options: HttpOptions): Promise<T> => {
        const token = await getAppToken();
        return await createRequest(path, options, token.accessToken);
    };

    /**
     * Anonymous request api
     * @param path - relative url
     * @param options - api options
     * @returns -
     */
    const publicRequest = async <T = any>(path: string, options: HttpOptions): Promise<T> => {
        return await createRequest(path, options);
    };

    const getAppToken = async (): Promise<AccessToken> => {
        // TODO: validate expiration
        if (isEmpty(accessToken)) {
            const token = await publicRequest<AccessToken>("authorize", {
                method: HTTP_METHOD.GET
            });

            dispatch(setAccessToken(token));

            return token;
        }

        return accessToken;
    };

    const createRequest = async <T>(path: string, options: HttpOptions, token?: string): Promise<T> => {
        if (token) {
            options.headers = { ...options.headers, Authorization: `Bearer ${token}` };
        }
        return await axios({
            method: options.method,
            url: `${Env.REACT_APP_API_URL}/${path}`,
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                ...options.headers
            },
            data: options.data
        }).then((res) => res.data);
    };

    const createUser = async (data: User): Promise<User> => {
        return await appProtectedRequest<User>("users", { method: HTTP_METHOD.POST, data });
    };

    const createOrganization = async (data: Organization): Promise<Organization> => {
        return await appProtectedRequest<Organization>("organizations", {
            method: HTTP_METHOD.POST,
            data: organizationToApi(data)
        });
    };

    const getAssessmentForm = async (assessmentFormId: number, languageId = 1): Promise<AssessmentForm> => {
        return await appProtectedRequest<AssessmentForm>(
            `assessmentforms/${assessmentFormId}?languageId=${languageId}`,
            {
                method: HTTP_METHOD.GET
            }
        );
    };

    const getAssessmentForms = async (): Promise<AssessmentForm[]> => {
        return await protectedRequest<AssessmentForm[]>("assessmentforms", {
            method: HTTP_METHOD.GET
        });
    };

    const getAssessment = async (assessmentId: number): Promise<Assessment> => {
        return await appProtectedRequest<Assessment>(`assessments/${assessmentId}`, {
            method: HTTP_METHOD.GET
        });
    };

    const initializeAssessment = async (
        locationId: number,
        examineeId: number,
        languageId: number,
        assessmentFormId: number,
        appointmentId?: number,
        isRetest?: boolean
    ): Promise<Assessment> => {
        return await protectedRequest<Assessment>(`locations/${locationId}/examinees/${examineeId}/assessments`, {
            method: HTTP_METHOD.POST,
            data: {
                languageId,
                assessmentFormId,
                appointmentId,
                isRetest
            } as AssessmentInitializeBody
        });
    };

    const uploadAssessmentResponse = async (assessmentId: number, wordResponse: WordResponseApi): Promise<boolean> => {
        const parsedWordResponse: Partial<WordResponseApi> = {
            stepName: wordResponse.stepName,
            targetWord: wordResponse.targetWord,
            responseType: wordResponse.responseType,
            tryNumber: wordResponse.tryNumber,
            source: wordResponse.source
        };

        if (wordResponse.audio) {
            parsedWordResponse.audio = new File([wordResponse.audio], ".wav", {
                type: wordResponse.audio.type
            });
        }

        if (wordResponse.response) {
            parsedWordResponse.response = wordResponse.response;
        }

        return await appProtectedRequest<boolean>(`assessments/${assessmentId}/responses`, {
            method: HTTP_METHOD.POST,
            data: parsedWordResponse,
            headers: {
                accept: "*/*",
                "Content-Type": "multipart/form-data"
            }
        });
    };

    const uploadAssessmentStepResponse = async (
        assessmentId: number,
        stepName: string,
        audio: Blob
    ): Promise<boolean> => {
        audio = new File([audio], ".wav", { type: audio.type });

        return await appProtectedRequest<boolean>(`assessments/${assessmentId}/responses/${stepName}/audio`, {
            method: HTTP_METHOD.POST,
            data: { audio },
            headers: {
                accept: "*/*",
                "Content-Type": "multipart/form-data"
            }
        });
    };

    const bulkAssessmentResponse = async (assessmentId: number, wordResponses: BulkResponseApi): Promise<boolean> => {
        return await appProtectedRequest<boolean>(`assessments/${assessmentId}/responses/bulk`, {
            method: HTTP_METHOD.POST,
            data: wordResponses
        });
    };

    const updateAssessmentStatus = async (
        locationId: number,
        assessmentId: number,
        status: AssessmentStatus
    ): Promise<void> => {
        return await appProtectedRequest(`locations/${locationId}/assessments/${assessmentId}/status`, {
            method: HTTP_METHOD.PUT,
            data: { status: status.replace(" ", "") }
        });
    };

    const reviewAssessment = async (
        locationId: number,
        assessmentId: number,
        note: string
    ): Promise<Entity<number>> => {
        return await protectedRequest(`locations/${locationId}/assessments/${assessmentId}/notes`, {
            method: HTTP_METHOD.POST,
            data: { note }
        });
    };

    const getOrganization = async (organizationId: number): Promise<OrganizationApiResponse> => {
        return await protectedRequest(`organizations/${organizationId}`, {
            method: HTTP_METHOD.GET
        });
    };

    const getOrganizations = async (
        pageNumber = 1,
        pageSize = 3000,
        sortBy?: getOrganizationsSortOptions[],
        sortDirection?: SortDirection,
        search?: string
    ): Promise<Organizations> => {
        const queryString = qs.stringify(
            { pageNumber, pageSize, sortBy, sortDirection, search },
            { arrayFormat: "repeat" }
        );
        return await protectedRequest(`organizations/?${queryString}`, {
            method: HTTP_METHOD.GET
        });
    };

    const getOrganizationLocations = async (organizationId: number): Promise<OrgLocation[]> => {
        return await protectedRequest(`organizations/${organizationId}/locations`, {
            method: HTTP_METHOD.GET
        });
    };

    const addOrganizationLocation = async (organizationId: number, orgLocation: OrgLocationApi): Promise<void> => {
        return await protectedRequest(`organizations/${organizationId}/locations`, {
            method: HTTP_METHOD.POST,
            data: orgLocation
        });
    };

    const editLocation = async (locationId: number, orgLocation: OrgLocationApi): Promise<void> => {
        const { id, ...dropId } = orgLocation;

        return await protectedRequest(`locations/${locationId}`, {
            method: HTTP_METHOD.PUT,
            data: dropId
        });
    };

    const updateOrganization = async (organizationId: number, orgName: string): Promise<void> => {
        return await protectedRequest(`organizations/${organizationId}`, {
            method: HTTP_METHOD.PUT,
            data: { name: orgName }
        });
    };

    const updateOrganizationStatus = async (
        organizationId: number,
        status: OrganizationStatus.active | OrganizationStatus.accessRevoked
    ): Promise<void> => {
        const apiStatus = getEnumKeyByValue(OrganizationStatus, status);
        return await protectedRequest(`organizations/${organizationId}/status`, {
            method: HTTP_METHOD.PUT,
            data: { status: apiStatus }
        });
    };

    const updateOrganizationPrimaryContact = async (
        organizationId: number,
        primaryContact: PersonalInfo
    ): Promise<void> => {
        return await protectedRequest(`organizations/${organizationId}/primaryContact`, {
            method: HTTP_METHOD.PUT,
            data: primaryContact
        });
    };

    const deleteOrganization = async (organizationId: number): Promise<void> => {
        return await protectedRequest(`organizations/${organizationId}`, {
            method: HTTP_METHOD.DELETE
        });
    };

    const assignLocationUser = async (locationId: number, users: InviteUser[]): Promise<void> => {
        return await protectedRequest(`locations/${locationId}/users`, {
            method: HTTP_METHOD.POST,
            data: users[0]
        });
    };

    const batchAssignLocationUser = async (
        organizationId: number,
        locationId: number,
        users: InviteUser[],
        roleId: number,
        clinicianId?: string
    ): Promise<void> => {
        const userData: any = { users, roleId };

        if (clinicianId) {
            userData.clinicianId = clinicianId;
        }

        return await protectedRequest(`organizations/${organizationId}/locations/${locationId}/users/batch`, {
            method: HTTP_METHOD.POST,
            data: userData
        });
    };

    const getOrganizationUsers = async (
        organizationId: number,
        locations?: number[],
        roles?: RoleId[],
        statuses?: OrganizationUserStatusId[],
        pageNumber = 1,
        pageSize = 999,
        search?: string,
        sortBy?: getOrganizationUsersSortOptions,
        sortDirection?: SortDirection
    ): Promise<OrganizationUsers> => {
        const queryString = qs.stringify(
            {
                location: locations,
                role: roles,
                status: statuses,
                pageNumber,
                pageSize,
                search,
                sortBy,
                sortDirection
            },
            { arrayFormat: "repeat" }
        );

        return await protectedRequest(`organizations/${organizationId}/users/?${queryString}`, {
            method: HTTP_METHOD.GET
        });
    };

    const deleteOrganizationUsers = async (organizationId: number, userId: string): Promise<void> => {
        return await protectedRequest(`organizations/${organizationId}/users/${userId}`, {
            method: HTTP_METHOD.DELETE
        });
    };

    const addExaminee = async (locationId: number, examinee: Examinee): Promise<NewExamineeId> => {
        const newExaminee = parseNewExamineeForApi(examinee);
        return await protectedRequest(`locations/${locationId}/examinees`, {
            method: HTTP_METHOD.POST,
            data: newExaminee
        });
    };

    const setAppointment = async (
        locationId: number,
        examineeId: number,
        date: Date
    ): Promise<NewUpcomingAssessmentResponse> => {
        const data = {
            scheduledFor: format(date, "yyyy-MM-dd")
        };

        return await protectedRequest(`locations/${locationId}/examinees/${examineeId}/appointments`, {
            method: HTTP_METHOD.POST,
            data
        });
    };

    const getLocationExaminees = async (
        locationId: number,
        pageNumber = 1,
        pageSize = 3000,
        search?: string,
        sortBy?: getLocationExamineesSortOptions,
        sortDirection?: SortDirection
    ): Promise<ExistingExaminees> => {
        const queryString = qs.stringify(
            {
                pageNumber,
                pageSize,
                search,
                sortBy,
                sortDirection
            },
            { arrayFormat: "repeat" }
        );
        return await protectedRequest(`locations/${locationId}/examinees/?${queryString}`, {
            method: HTTP_METHOD.GET
        });
    };

    const getUpcomingAssessments = async (locationId: number): Promise<UpcomingAssessment[]> => {
        return await protectedRequest(`locations/${locationId}/appointments`, {
            method: HTTP_METHOD.GET
        });
    };

    const getExamineeDetails = async (locationId: number, examineeId: number): Promise<ExistingExamineeDetails> => {
        return await protectedRequest(`locations/${locationId}/examinees/${examineeId}`, {
            method: HTTP_METHOD.GET
        });
    };

    const getAssessments = async (
        locationId: number,
        archived?: boolean,
        status?: AssessmentStatus,
        pageNumber = 1,
        pageSize = 3000,
        sortBy = "date"
    ): Promise<ExistingAssessments> => {
        return await protectedRequest(
            `locations/${locationId}/assessments?pageNumber=${pageNumber}&pageSize=${pageSize}&sortBy=${sortBy}${
                status ? "&status=" + status : ""
            }${archived ? "&archived=true" : ""}`,
            {
                method: HTTP_METHOD.GET
            }
        );
    };

    const getAssessmentResults = async (locationId: number, assessmentId: number): Promise<AssessmentResult> => {
        return await protectedRequest(`locations/${locationId}/assessments/${assessmentId}/results`, {
            method: HTTP_METHOD.GET
        });
    };

    const getAssessmentHistoryByExaminee = async (
        locationId: number,
        examineeId: number
    ): Promise<AssessmentHistory[]> => {
        return await protectedRequest(`locations/${locationId}/examinees/${examineeId}/assessments`, {
            method: HTTP_METHOD.GET
        });
    };

    const downloadAssessments = async (
        locationId: number,
        assessmentIds: number[],
        fileFormat?: "csv"
    ): Promise<Blob> => {
        const queryString = qs.stringify({ assessmentId: assessmentIds, fileFormat }, { arrayFormat: "repeat" });

        return await protectedRequest<Blob>(`locations/${locationId}/assessments/results?${queryString}`, {
            method: HTTP_METHOD.GET
        });
    };

    const deleteAssessments = async (
        locationId: number,
        assessmentIds: number[],
        deletionMode?: AssessmentDeletionMode
    ): Promise<void> => {
        const queryString = qs.stringify({ deletionMode, assessmentId: assessmentIds }, { arrayFormat: "repeat" });

        return await protectedRequest(`locations/${locationId}/assessments/?${queryString}`, {
            method: HTTP_METHOD.DELETE
        });
    };

    const updateExaminee = async (locationId: number, examineeId: number, details: NewExamineeApi): Promise<void> => {
        return await protectedRequest(`locations/${locationId}/examinees/${examineeId}`, {
            method: HTTP_METHOD.PUT,
            data: details
        });
    };

    const getMyPermission = async (): Promise<UserDetail> => {
        return await protectedRequest("users/me", {
            method: HTTP_METHOD.GET
        });
    };

    const getUser = async (email: string): Promise<UserInfo> => {
        return await appProtectedRequest(`users/${email}`, {
            method: HTTP_METHOD.GET
        });
    };

    const deleteUserRoleByLocation = async (userId: string, roleId: number, locationId: number): Promise<void> => {
        return await protectedRequest(`locations/${locationId}/users/${userId}?role=${roleId}`, {
            method: HTTP_METHOD.DELETE
        });
    };

    const getAssessmentsPricings = async (): Promise<PricingTier[]> => {
        return await appProtectedRequest("purchases/pricing", {
            method: HTTP_METHOD.GET
        });
    };

    const createPurchase = async (
        organizationId: number,
        assessmentQuantity: number,
        offline?: boolean,
        unitPrice?: number,
        discountPercentage?: number
    ): Promise<string> => {
        return await protectedRequest<CreatePurchaseApiResponse>(
            `organizations/${organizationId}/purchases?offline=${!!offline}`,
            {
                method: HTTP_METHOD.POST,
                data: { assessmentQuantity, unitPrice, discountPercentage }
            }
        ).then((r) => r.checkoutSessionId);
    };

    const getPurchases = async (
        organizationId: number,
        pageNumber = 1,
        pageSize = 100,
        statuses?: PurchaseStatus[]
    ): Promise<Paginated<Purchase>> => {
        const queryString = qs.stringify({ pageNumber, pageSize, status: statuses }, { arrayFormat: "repeat" });
        return await protectedRequest(`organizations/${organizationId}/purchases/?${queryString}`, {
            method: HTTP_METHOD.GET
        });
    };

    const updateTargetResponse = async (
        assessmentId: number,
        targetResponseId: number,
        responseType: WordResponseType
    ): Promise<void> => {
        return await protectedRequest(`assessments/${assessmentId}/targetResponses/${targetResponseId}`, {
            method: HTTP_METHOD.PUT,
            data: {
                responseType
            }
        });
    };

    const updateAssessmentResult = async (locationId: number, assessmentId: number): Promise<void> => {
        return await protectedRequest(`locations/${locationId}/assessments/${assessmentId}/results`, {
            method: HTTP_METHOD.PUT
        });
    };

    const getAvailableAssessments = async (organizationId: number): Promise<AvailableAssessment> => {
        return await protectedRequest(`organizations/${organizationId}/availableAssessments`, {
            method: HTTP_METHOD.GET
        });
    };

    const updateUser = async (email: string, user: UserUpdate): Promise<void> => {
        return await appProtectedRequest(`users/${email}`, {
            method: HTTP_METHOD.PUT,
            data: user
        });
    };

    const updateLocationRole = async (
        email: string,
        organizationId: number,
        locationId: number,
        roleId: number
    ): Promise<void> => {
        return await appProtectedRequest(
            `organizations/${organizationId}/locations/${locationId}/users/${encodeURI(email)}/roles/${roleId}`,
            {
                method: HTTP_METHOD.PUT
            }
        );
    };

    const createTargetResponseNote = async (
        assessmentId: number,
        targetResponseId: number,
        note: string
    ): Promise<Entity<number>> => {
        return await protectedRequest(`assessments/${assessmentId}/targetResponses/${targetResponseId}/notes`, {
            method: HTTP_METHOD.POST,
            data: { note }
        });
    };

    const sendEmailContactSales = async (
        organization: Organization,
        user: User,
        title: string,
        jobTitle: string,
        country: string,
        details: string
    ): Promise<Entity<number>> => {
        return await appProtectedRequest("notifications/contactsales", {
            method: HTTP_METHOD.POST,
            data: {
                email: user.email,
                firstName: user.firstName,
                lastName: user.lastName,
                title: user.title ?? title,
                jobTitle: user.jobTitle ?? jobTitle,
                phoneNumber: organization?.primaryContact?.phoneNumber,
                organizationId: organization.id,
                organizationName: organization.orgName,
                country: organization?.address?.country ?? country,
                details: organization.details ?? details
            }
        });
    };

    const getDocumentations = async (): Promise<Documentation[]> => {
        return await protectedRequest("documentations", {
            method: HTTP_METHOD.GET
        });
    };

    const createDocumentation = async (
        title: string,
        description: string,
        type: DocumentationCategory,
        file?: File
    ): Promise<void> => {
        await protectedRequest<boolean>("documentations", {
            method: HTTP_METHOD.POST,
            data: {
                title,
                description,
                category: type,
                file
            },
            headers: {
                accept: "*/*",
                "Content-Type": "multipart/form-data"
            }
        });
    };

    const connectionTest = async (): Promise<boolean> => {
        const before = Date.now();
        const baseUrl = `${Env.REACT_APP_BLOB_URL}/instructions-en-us/download_test?`;

        const promises = [1, 2, 3].map(async (_) => await axios.get(`${baseUrl}${Date.now()}`));

        let elapsed = ELAPSED_TIMEOUT;

        try {
            await Promise.all(promises);
            elapsed = Date.now() - before;
        } catch {}

        return elapsed < ELAPSED_TIMEOUT;
    };

    const getWordRequests = async (pageNumber = 1, pageSize = 100): Promise<WordRequests> => {
        const queryString = qs.stringify({ pageNumber, pageSize }, { arrayFormat: "repeat" });

        return await protectedRequest(`words/requests?${queryString}`, {
            method: HTTP_METHOD.GET
        });
    };

    const setAlternateForWord = async (word: string, newAlternate: string): Promise<void> => {
        return await protectedRequest(`words/${word}/alternates`, {
            method: HTTP_METHOD.POST,
            data: { value: newAlternate }
        });
    };

    const getRoles = async (): Promise<Role[]> => {
        return await protectedRequest("roles", {
            method: HTTP_METHOD.GET
        });
    };

    const getAgreements = async (): Promise<Agreement[]> => {
        return await appProtectedRequest("agreements", {
            method: HTTP_METHOD.GET
        });
    };

    const createUserAgreement = async (
        agreementId: number,
        userId: string,
        locationId: number,
        roleId: number
    ): Promise<void> => {
        return await appProtectedRequest(`agreements/${agreementId}/users/${userId}`, {
            data: { locationId, roleId },
            method: HTTP_METHOD.POST
        });
    };

    return {
        createUser,
        createOrganization,
        getAssessmentForm,
        getAssessmentForms,
        uploadAssessmentResponse,
        uploadAssessmentStepResponse,
        bulkAssessmentResponse,
        getAssessment,
        initializeAssessment,
        updateAssessmentStatus,
        reviewAssessment,
        getOrganization,
        getOrganizations,
        getOrganizationLocations,
        addOrganizationLocation,
        editLocation,
        updateOrganization,
        updateOrganizationStatus,
        updateOrganizationPrimaryContact,
        deleteOrganization,
        assignLocationUser,
        getOrganizationUsers,
        getAppToken,
        batchAssignLocationUser,
        deleteOrganizationUsers,
        addExaminee,
        setAppointment,
        getLocationExaminees,
        getUpcomingAssessments,
        getExamineeDetails,
        getAssessments,
        getAssessmentResults,
        downloadAssessments,
        deleteAssessments,
        updateExaminee,
        getAssessmentHistoryByExaminee,
        getMyPermission,
        deleteUserRoleByLocation,
        getAssessmentsPricings,
        createPurchase,
        getPurchases,
        getUser,
        updateTargetResponse,
        updateAssessmentResult,
        getAvailableAssessments,
        updateUser,
        updateLocationRole,
        createTargetResponseNote,
        sendEmailContactSales,
        getDocumentations,
        createDocumentation,
        connectionTest,
        getWordRequests,
        setAlternateForWord,
        getRoles,
        getAgreements,
        createUserAgreement
    };
};

interface AssessmentInitializeBody {
    languageId: number;
    assessmentFormId: number;
}

export enum HTTP_METHOD {
    GET = "get",
    POST = "post",
    PUT = "put",
    PATCH = "patch",
    DELETE = "delete"
}

interface HttpOptions {
    method: HTTP_METHOD;
    data?: any;
    headers?: any;
}

export interface ApiServiceProps {
    createUser: (data: User) => Promise<User>;
    createOrganization: (data: Organization) => Promise<Organization>;
    getAssessment: (assessmentId: number) => Promise<Assessment>;
    getAssessmentForm: (assessmentFormId: number, languageId?: number) => Promise<AssessmentForm>;
    getAssessmentForms: () => Promise<AssessmentForm[]>;
    uploadAssessmentResponse: (assessmentId: number, wordResponse: WordResponseApi) => Promise<boolean>;
    uploadAssessmentStepResponse: (assessmentId: number, stepName: string, audio: Blob) => Promise<boolean>;
    bulkAssessmentResponse: (assessmentId: number, wordResponses: BulkResponseApi) => Promise<boolean>;
    initializeAssessment: (
        locationId: number,
        examineeId: number,
        languageId: number,
        assessmentFormId: number,
        appointmentId?: number,
        isRetest?: boolean
    ) => Promise<Assessment>;
    updateAssessmentStatus: (locationId: number, assessmentId: number, status: AssessmentStatus) => Promise<void>;
    reviewAssessment: (locationId: number, assessmentId: number, note: string) => Promise<Entity<number>>;
    getOrganization: (organizationId: number) => Promise<OrganizationApiResponse>;
    getOrganizations: (
        pageNumber?: number,
        pageSize?: number,
        sortBy?: getOrganizationsSortOptions[],
        sortDirection?: SortDirection,
        search?: string
    ) => Promise<Organizations>;
    getOrganizationLocations: (organizationId: number) => Promise<OrgLocation[]>;
    addOrganizationLocation: (organizationId: number, orgLocation: OrgLocationApi) => Promise<void>;
    editLocation: (locationId: number, orgLocation: OrgLocationApi) => Promise<void>;
    updateOrganization: (organizationId: number, orgName: string) => Promise<void>;
    updateOrganizationStatus: (
        organizationId: number,
        status: OrganizationStatus.active | OrganizationStatus.accessRevoked
    ) => Promise<void>;
    deleteOrganization: (organizationId: number) => Promise<void>;
    updateOrganizationPrimaryContact: (organizationId: number, primaryContact: PersonalInfo) => Promise<void>;
    assignLocationUser: (locationId: number, users: InviteUser[]) => Promise<void>;
    getOrganizationUsers: (
        organizationId: number,
        locations?: number[],
        roles?: RoleId[],
        statuses?: OrganizationUserStatusId[],
        pageNumber?: number,
        pageSize?: number,
        search?: string,
        sortBy?: getOrganizationUsersSortOptions,
        sortDirection?: SortDirection
    ) => Promise<OrganizationUsers>;
    getAppToken: () => Promise<AccessToken>;
    batchAssignLocationUser: (
        organizationId: number,
        locationId: number,
        users: InviteUser[],
        roleId: RoleId,
        clinicianId?: string
    ) => Promise<void>;
    deleteOrganizationUsers: (organizationId: number, userId: string) => Promise<void>;
    addExaminee: (locationId: number, examinee: Examinee) => Promise<NewExamineeId>;
    setAppointment: (locationId: number, examineeId: number, date: Date) => Promise<NewUpcomingAssessmentResponse>;
    getLocationExaminees: (
        locationId: number,
        pageNumber?: number,
        pageSize?: number,
        search?: string,
        sortBy?: getLocationExamineesSortOptions,
        sortDirection?: SortDirection
    ) => Promise<ExistingExaminees>;
    getUpcomingAssessments: (locationId: number) => Promise<UpcomingAssessment[]>;
    getExamineeDetails: (locationId: number, examineeId: number) => Promise<ExistingExamineeDetails>;
    getAssessments: (
        locationId: number,
        archived?: boolean,
        status?: AssessmentStatus,
        pageNumber?: number,
        pageSize?: number,
        sortBy?: string
    ) => Promise<ExistingAssessments>;
    getAssessmentResults: (locationId: number, assessmentId: number) => Promise<AssessmentResult>;
    downloadAssessments: (locationId: number, assessmentIds: number[], fileFormat?: "csv") => Promise<Blob>;
    deleteAssessments: (
        locationId: number,
        assessmentIds: number[],
        deletionMode?: AssessmentDeletionMode
    ) => Promise<void>;
    updateExaminee: (locationId: number, examineeId: number, details: NewExamineeApi) => Promise<void>;
    getAssessmentHistoryByExaminee: (locationId: number, examineeId: number) => Promise<AssessmentHistory[]>;
    getMyPermission: () => Promise<UserDetail>;
    deleteUserRoleByLocation: (userId: string, roleId: number, locationId: number) => Promise<void>;
    getAssessmentsPricings: () => Promise<PricingTier[]>;
    createPurchase: (
        organizationId: number,
        assessmentQuantity: number,
        offline?: boolean,
        unitPrice?: number,
        discountPercentage?: number
    ) => Promise<string>;
    getPurchases: (
        organizationId: number,
        pageNumber?: number,
        pageSize?: number,
        statuses?: PurchaseStatus[]
    ) => Promise<Paginated<Purchase>>;
    getUser: (email: string) => Promise<UserInfo>;
    updateTargetResponse: (
        assessmentId: number,
        targetResponseId: number,
        responseType: WordResponseType
    ) => Promise<void>;
    updateAssessmentResult: (locationId: number, assessmentId: number) => Promise<void>;
    getAvailableAssessments: (organizationId: number) => Promise<AvailableAssessment>;
    updateUser: (email: string, user: UserUpdate) => Promise<void>;
    updateLocationRole: (email: string, organizationId: number, locationId: number, roleId: number) => Promise<void>;
    createTargetResponseNote: (assessmentId: number, targetResponseId: number, note: string) => Promise<Entity<number>>;
    sendEmailContactSales: (
        organization: Organization,
        user: User,
        title: string,
        jobTitle: string,
        country: string,
        details: string
    ) => Promise<Entity<number>>;
    getDocumentations: () => Promise<Documentation[]>;
    createDocumentation: (
        title: string,
        description: string,
        type: DocumentationCategory,
        file?: File
    ) => Promise<void>;
    connectionTest: () => Promise<boolean>;
    getWordRequests: (pageNumber?: number, pageSize?: number) => Promise<WordRequests>;
    setAlternateForWord: (word: string, newAlternate: string) => Promise<void>;
    getRoles: () => Promise<Role[]>;
    getAgreements: () => Promise<Agreement[]>;
    createUserAgreement: (agreementId: number, userId: string, locationId: number, roleId: number) => Promise<void>;
}
